import React, { useCallback, useEffect, useState } from 'react';
import {
  Shield, RefreshCw, Download, AlertTriangle, Cpu, Layers, Activity, FileText, FileDown,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { adminService } from '../services/adminService';
import { formatCompactNumber, formatNumber, formatPercent, formatDateTime } from '../utils/format';
import { exportToCSV, exportToPDF, formatCurrencyForExport } from '../utils/exportUtils';
import './AdminPage.css';

const BioKinetic: React.FC = () => {
  const [hours, setHours] = useState(24);
  const [stats, setStats] = useState<any | null>(null);
  const [config, setConfig] = useState<any | null>(null);
  const [flagged, setFlagged] = useState<any[]>([]);
  const [reasonCodes, setReasonCodes] = useState<any | null>(null);
  const [clusters, setClusters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportDay, setExportDay] = useState<string>(new Date().toISOString().slice(0, 10));
  const [exportStatus, setExportStatus] = useState<string>('');
  const [liveSessions, setLiveSessions] = useState<any[]>([]);
  const [report, setReport] = useState<any | null>(null);
  const [reportPeriod, setReportPeriod] = useState<'weekly' | 'monthly'>('weekly');
  const [reportLoading, setReportLoading] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    const [s, c, f, r, dc] = await Promise.allSettled([
      adminService.getFraudStats(hours),
      adminService.getFraudConfig(),
      adminService.getFlaggedUsers({ hours, limit: 50 }),
      adminService.getFraudReasonCodes(hours),
      adminService.getDeviceClusters({ limit: 50 }),
    ]);
    if (s.status === 'fulfilled') setStats(s.value);
    if (c.status === 'fulfilled') setConfig(c.value);
    if (f.status === 'fulfilled') {
      const list = f.value?.flagged_users || f.value || [];
      setFlagged(Array.isArray(list) ? list : []);
    }
    if (r.status === 'fulfilled') setReasonCodes(r.value);
    if (dc.status === 'fulfilled') setClusters(dc.value?.clusters || []);
    setLoading(false);
    setRefreshing(false);
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  // Live sessions panel — poll every 20s.
  const loadLive = useCallback(async () => {
    try {
      const data = await adminService.getFraudLiveSessions({ limit: 50 });
      setLiveSessions(data?.sessions || []);
    } catch { /* transient — keep last snapshot */ }
  }, []);

  useEffect(() => {
    loadLive();
    const id = setInterval(loadLive, 20000);
    return () => clearInterval(id);
  }, [loadLive]);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      setReport(await adminService.getFraudPeriodReport(reportPeriod));
    } catch {
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  }, [reportPeriod]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Flatten the report's payouts-prevented (by currency) into export rows.
  const reportRows = report ? [
    ...Object.entries(report.payouts_prevented || {}).map(([currency, amount]) => ({
      metric: `Payouts prevented (${currency})`,
      value: formatCurrencyForExport(Number(amount), currency),
    })),
    { metric: 'Sessions scanned', value: String(report.sessions_scanned ?? 0) },
    { metric: 'Sessions flagged', value: String(report.sessions_flagged ?? 0) },
    { metric: '% flagged', value: `${report.pct_flagged ?? 0}%` },
    { metric: 'Device clusters found', value: String(report.device_clusters_found ?? 0) },
    { metric: 'False positive rate', value: report.false_positive_rate == null ? 'n/a' : `${(report.false_positive_rate * 100).toFixed(2)}%` },
    { metric: 'Appeals overturned', value: String(report.appeals_overturned_count ?? 0) },
    { metric: 'Appeals filed', value: String(report.appeals_filed ?? 0) },
  ] : [];

  const reportColumns = [
    { key: 'metric', header: 'Metric' },
    { key: 'value', header: 'Value' },
  ];

  const handleReportCSV = () => exportToCSV(reportRows, reportColumns, `fraud-report-${reportPeriod}`);
  const handleReportPDF = () => exportToPDF(
    reportRows, reportColumns,
    `Fraud Engine Report (${reportPeriod}) — ${report?.start_date} to ${report?.end_date}`,
    `fraud-report-${reportPeriod}`,
  );

  const handleExport = async () => {
    setExportStatus('Downloading…');
    try {
      const blob = await adminService.getMoatExport(exportDay);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moat-${exportDay}.jsonl`;
      a.click();
      URL.revokeObjectURL(url);
      setExportStatus('Saved.');
      setTimeout(() => setExportStatus(''), 2500);
    } catch (e: any) {
      setExportStatus(`Failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`);
    }
  };

  // Synthesise a trust-score histogram from flagged users' avg_fraud_score
  const histogram = (() => {
    const buckets = [
      { range: '0-20', count: 0 }, { range: '20-40', count: 0 },
      { range: '40-60', count: 0 }, { range: '60-80', count: 0 },
      { range: '80-100', count: 0 },
    ];
    flagged.forEach((u: any) => {
      const v = Number(u.avg_fraud_score);
      if (!Number.isFinite(v)) return;
      const idx = Math.min(4, Math.floor(v / 20));
      buckets[idx].count += u.flagged_games || 1;
    });
    return buckets;
  })();

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Shield size={20} style={{ marginRight: 8 }} />Bio-Kinetic Fraud</h1>
          <p className="page-subtitle">Trust scoring, flagged users, and Moat telemetry export</p>
        </div>
        <div className="header-actions">
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <option value={6}>Last 6h</option>
            <option value={24}>Last 24h</option>
            <option value={72}>Last 72h</option>
            <option value={168}>Last 7d</option>
          </select>
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {stats?.note && (
        <div className="panel">
          <div className="panel-body" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertTriangle size={16} color="#f59e0b" />
            <span style={{ fontSize: '0.85rem', color: '#92400e' }}>{stats.note}</span>
          </div>
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Games analyzed</span>
          <span className="kpi-value">{formatCompactNumber(stats?.total_games_analyzed ?? 0)}</span>
          <span className="kpi-detail">{hours}h window</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Flagged games</span>
          <span className="kpi-value">{formatCompactNumber(stats?.flagged_games ?? 0)}</span>
          <span className="kpi-detail">{formatPercent(stats?.flag_rate_percent ?? 0, 2)} flag rate</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg trust score</span>
          <span className="kpi-value">
            {stats?.average_trust_score != null ? Number(stats.average_trust_score).toFixed(1) : '—'}
          </span>
          <span className="kpi-detail">0 = bot, 100 = human</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Engine status</span>
          <span className="kpi-value">{config?.status || 'unknown'}</span>
          {config?.moat?.fraud_moat_pow_difficulty_bits != null && (
            <span className="kpi-detail">PoW {config.moat.fraud_moat_pow_difficulty_bits} bits</span>
          )}
        </div>
      </div>

      <div className="charts-row-2">
        <div className="panel">
          <div className="panel-header">
            <h3><Cpu size={16} /> Trust score distribution</h3>
          </div>
          <div className="panel-body">
            {loading ? <div className="skeleton" style={{ height: 240 }} /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={histogram}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Flagged games" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="panel">
          <div className="panel-header">
            <h3><Download size={16} /> Moat ingest export</h3>
          </div>
          <div className="panel-body">
            <p style={{ marginTop: 0, color: '#64748b', fontSize: '0.85rem' }}>
              Download the local Moat ingest shard for a UTC day. Use this to feed offline ML training.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                value={exportDay}
                onChange={(e) => setExportDay(e.target.value)}
              />
              <button className="btn" onClick={handleExport}>
                <Download size={14} /> Download JSONL
              </button>
              {exportStatus && <span style={{ fontSize: '0.85rem', color: '#475569' }}>{exportStatus}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3><AlertTriangle size={16} /> Flagged users</h3>
          <span className="kpi-detail">Top {flagged.length} in window</span>
        </div>
        <div className="panel-body tight">
          {flagged.length === 0 ? (
            <div className="empty">No flagged users in this window</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>User ID</th><th>Email</th><th>Name</th>
                  <th>Flagged games</th><th>Avg trust</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map((u: any) => (
                  <tr key={u.user_id}>
                    <td>#{u.user_id}</td>
                    <td>{u.email || '—'}</td>
                    <td>{u.full_name || '—'}</td>
                    <td>{formatNumber(u.flagged_games)}</td>
                    <td>
                      <span className={`pill ${
                        u.avg_fraud_score == null ? '' :
                        u.avg_fraud_score < 40 ? 'error' :
                        u.avg_fraud_score < 70 ? 'warning' : 'healthy'
                      }`}>
                        {u.avg_fraud_score != null ? Number(u.avg_fraud_score).toFixed(1) : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="charts-row-2">
        <div className="panel">
          <div className="panel-header">
            <h3><AlertTriangle size={16} /> Reason code breakdown</h3>
            <span className="kpi-detail">{reasonCodes?.sessions_scanned ?? 0} sessions scanned</span>
          </div>
          <div className="panel-body tight">
            {!reasonCodes || Object.keys(reasonCodes.reason_code_counts || {}).length === 0 ? (
              <div className="empty">No reason codes recorded in this window</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Reason code</th><th>Count</th></tr>
                </thead>
                <tbody>
                  {Object.entries(reasonCodes.reason_code_counts as Record<string, number>).map(([code, count]) => (
                    <tr key={code}>
                      <td><code>{code}</code></td>
                      <td>{formatNumber(count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {reasonCodes?.avg_latency_ms != null && (
              <p style={{ marginTop: 8, fontSize: '0.8rem', color: '#64748b' }}>
                Avg fraud engine latency: {reasonCodes.avg_latency_ms}ms (budget: 50ms)
              </p>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <h3><Layers size={16} /> Device clusters</h3>
            <span className="kpi-detail">{clusters.length} found</span>
          </div>
          <div className="panel-body tight">
            {clusters.length === 0 ? (
              <div className="empty">No device clusters found (nightly scan job populates this)</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Signal</th><th>Key</th><th>Size</th><th>Severity</th></tr>
                </thead>
                <tbody>
                  {clusters.map((c: any) => (
                    <tr key={c.id}>
                      <td>{c.signal_type}</td>
                      <td><code>{c.cluster_key}</code></td>
                      <td>{formatNumber(c.size)}</td>
                      <td>
                        <span className={`pill ${
                          c.severity === 'high' ? 'error' : c.severity === 'medium' ? 'warning' : 'healthy'
                        }`}>
                          {c.severity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {config && (
        <div className="panel">
          <div className="panel-header">
            <h3>Engine configuration</h3>
            <span className="kpi-detail">Last checked {formatDateTime(new Date())}</span>
          </div>
          <div className="panel-body">
            <pre style={{ background: '#f8fafc', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12 }}>
              {JSON.stringify(config, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Period report — headlined by payouts prevented in real currency */}
      <div className="panel">
        <div className="panel-header">
          <h3><FileText size={16} /> Fraud report</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value as 'weekly' | 'monthly')}>
              <option value="weekly">Last 7 days</option>
              <option value="monthly">Last 30 days</option>
            </select>
            <button className="btn-secondary" onClick={loadReport} disabled={reportLoading}>
              <RefreshCw size={14} className={reportLoading ? 'spin' : ''} /> Refresh
            </button>
            <button className="btn-secondary" onClick={handleReportCSV} disabled={!report}>
              <FileDown size={14} /> CSV
            </button>
            <button className="btn-secondary" onClick={handleReportPDF} disabled={!report}>
              <Download size={14} /> PDF
            </button>
          </div>
        </div>
        <div className="panel-body">
          {!report ? (
            <div className="empty">No report data yet. Run the nightly summary or use “Recompute day”.</div>
          ) : (
            <>
              <div className="kpi-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
                {Object.keys(report.payouts_prevented || {}).length === 0 ? (
                  <div className="kpi-card">
                    <div className="kpi-label">Payouts prevented</div>
                    <div className="kpi-value">—</div>
                  </div>
                ) : (
                  Object.entries(report.payouts_prevented).map(([currency, amount]) => (
                    <div className="kpi-card" key={currency}>
                      <div className="kpi-label">Payouts prevented ({currency})</div>
                      <div className="kpi-value">{formatCurrencyForExport(Number(amount), currency)}</div>
                    </div>
                  ))
                )}
                <div className="kpi-card">
                  <div className="kpi-label">Sessions scanned</div>
                  <div className="kpi-value">{formatCompactNumber(report.sessions_scanned || 0)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">% flagged</div>
                  <div className="kpi-value">{report.pct_flagged ?? 0}%</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">False positive rate</div>
                  <div className="kpi-value">{report.false_positive_rate == null ? 'n/a' : `${(report.false_positive_rate * 100).toFixed(2)}%`}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Appeals overturned</div>
                  <div className="kpi-value">{report.appeals_overturned_count ?? 0}</div>
                </div>
              </div>
              {/* Attestation coverage — the evidence behind whether a MISSING
                  attestation means anything. Absence only becomes a signal once
                  most of a platform's cohort demonstrably can attest, so read
                  this before setting the coverage threshold in backend config. */}
              <div className="kpi-detail" style={{ marginTop: 4, marginBottom: 6 }}>
                Hardware attestation coverage
              </div>
              {Object.keys(report.attestation_coverage || {}).length === 0 ? (
                <div className="empty">
                  No attestation data yet — enable it in Config → Fraud Engine and provision
                  credentials (see docs/FRAUD_ATTESTATION_SETUP.md).
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Sessions</th>
                      <th>Coverage</th>
                      <th>Passed</th>
                      <th>Failed</th>
                      <th>No verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.attestation_coverage).map(([platform, c]: [string, any]) => (
                      <tr key={platform}>
                        <td>{platform}</td>
                        <td>{formatCompactNumber(c.total || 0)}</td>
                        <td><strong>{c.coverage_pct ?? 0}%</strong></td>
                        <td>{formatCompactNumber(c.passed || 0)}</td>
                        <td>{formatCompactNumber(c.failed || 0)}</td>
                        <td>{formatCompactNumber((c.absent || 0) + (c.stale || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="kpi-detail">
                {report.start_date} → {report.end_date} · {report.days_covered} day(s) of aggregated data
              </div>
            </>
          )}
        </div>
      </div>

      {/* Live sessions — polls every 20s */}
      <div className="panel">
        <div className="panel-header">
          <h3><Activity size={16} /> Live sessions</h3>
          <span className="kpi-detail">{liveSessions.length} recent · auto-refresh 20s</span>
        </div>
        <div className="panel-body tight">
          {liveSessions.length === 0 ? (
            <div className="empty">No scored sessions yet</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Risk</th>
                  <th>Action</th>
                  <th>Reason codes</th>
                  <th>Latency</th>
                </tr>
              </thead>
              <tbody>
                {liveSessions.map((s: any) => (
                  <tr key={s.session_id}>
                    <td>{s.created_at ? formatDateTime(s.created_at) : '—'}</td>
                    <td>{s.user_email || (s.user_id ? `#${s.user_id}` : '—')}</td>
                    <td><strong>{s.risk_score}</strong></td>
                    <td><span className={`status-badge status-${(s.recommended_action || '').toLowerCase()}`}>{s.recommended_action}</span></td>
                    <td>{(s.reason_codes || []).join(', ') || '—'}</td>
                    <td>{s.latency_ms != null ? `${s.latency_ms.toFixed?.(1) ?? s.latency_ms} ms` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default BioKinetic;
